import { Component, Host, h, Element, Prop, Watch } from '@stencil/core';

export interface AhaPageThumbnail extends HTMLAhaPageThumbnailElement {}

/**
 * aha-page-thumbnail component, used to display pages (notes/whiteboards).
 * @slot default - Contents of the default slot, used to display the thumbnail image.
 * @slot file-type-icon - Contents of the file-type-icon slot, used to display the emoji or file icon.
 * @slot shared-icon - Contents of the shared-icon slot.
 */
@Component({
  tag: 'aha-page-thumbnail',
  styleUrl: 'aha-page-thumbnail.css',
  shadow: true,
})
export class AhaPageThumbnail {
  @Element() host!: HTMLAhaPageThumbnailElement;

  private defaultSlotElement!: HTMLSlotElement;

  // Weak set of elements that have had their alt text auto-generated from the name prop. This prevents user-defined alts from being overwritten.
  private imagesWithGeneratedAlt = new WeakSet<HTMLElement>();
  // Flag to prevent infinite recursion
  private isUpdating = false;
  /**
   * Specifies the URL of the page the link goes to.
   */
  @Prop() href!: string;

  /**
   * The date the page was last accessed.
   */
  @Prop() lastAccessedAt: string | undefined;

  /**
   * The name of the page.
   */
  @Prop() name: string | undefined;
  @Watch('name')
  nameChanged(newName?: string | undefined) {
    if (this.isUpdating) return; // Prevent recursion
    this.isUpdating = true;

    if (this.defaultSlotElement) {
      this.defaultSlotElement.assignedElements().forEach(elem => {
        if (
          elem instanceof HTMLImageElement &&
          this.name &&
          (this.imagesWithGeneratedAlt.has(elem) || !elem.alt)
        ) {
          this.imagesWithGeneratedAlt.add(elem);
          elem.alt = this.name || newName || '';
        }
      });
    }

    this.isUpdating = false; // Reset flag
  }

  /**
   * Specifies the ajax method to use
   */
  @Prop() post!: boolean;

  /**
   * Whether the `<aha-page-thumbnail>` is selected or not.
   */
  @Prop({ reflect: true }) selected!: boolean;

  /**
   * Whether to show the shared icon.
   */
  @Prop() showSharedIcon: boolean | undefined;

  /**
   * The URL of the thumbnail to display.
   */
  @Prop() thumbnailUrl: string | undefined;

  handleSlotChange = () => {
    if (this.name) {
      this.nameChanged(this.name);
    }
  };

  render() {
    const content = () => (
      <div id="content">
        <div id="image-wrapper">
          <slot
            ref={e => (this.defaultSlotElement = e as HTMLSlotElement)}
            onSlotchange={this.handleSlotChange}
          ></slot>
        </div>
        <div id="page-thumbnail--title">
          <span>
            <slot name="file-type-icon" />
          </span>
          {this.name}
        </div>
        {this.lastAccessedAt && (
          <div id="page-thumbnail--last-accessed">
            <span>
              Last accessed:{' '}
              <aha-date date={this.lastAccessedAt || new Date().toISOString()} format="medium" />
            </span>
          </div>
        )}
        {this.showSharedIcon && (
          <div id="page-thumbnail--share-link">
            <slot name="shared-icon" />
          </div>
        )}

        <div class="overlay">
          <slot name="overlay"></slot>
        </div>
      </div>
    );

    return (
      <Host>
        {this.href ? (
          <a href={`${this.href}`} class="tile" data-method={`${this.post ? 'post' : undefined}`}>
            {content()}
          </a>
        ) : (
          content()
        )}
      </Host>
    );
  }
}
